import type { Campaign, Media } from '@/payload-types'
import { generateHref } from '@/lib/generate-href'

function getCampaignYear(campaign: Campaign): number {
  const yearFromDateString = /^(\d{4})/.exec(campaign.endDate)?.[1]
  if (yearFromDateString) return Number(yearFromDateString)

  const timestamp = Date.parse(campaign.endDate)
  if (Number.isNaN(timestamp)) return Number.NEGATIVE_INFINITY

  return new Date(timestamp).getUTCFullYear()
}

export function sortCampaignsByYearThenOrder(campaigns: Campaign[]): Campaign[] {
  return [...campaigns].sort((a, b) => {
    const yearDiff = getCampaignYear(b) - getCampaignYear(a)
    if (yearDiff !== 0) return yearDiff

    const orderA = a._order ?? ''
    const orderB = b._order ?? ''
    if (orderA !== orderB) return orderA.localeCompare(orderB)

    return a.name.localeCompare(b.name)
  })
}

export function getCampaignImage(
  campaign: Campaign,
): { url: string; width?: number; height?: number } | null {
  const image = campaign.image
  if (!image || typeof image === 'string') return null
  const media = image as Media
  const size = media.sizes?.medium || media.sizes?.large
  const url = size?.url || media.url
  if (!url) return null
  return {
    url,
    width: size?.width || media.width || undefined,
    height: size?.height || media.height || undefined,
  }
}

export function getCampaignLinkHref(campaign: Campaign): string {
  const linkType = (campaign as Campaign & { linkType?: 'external' | 'iframe' | null }).linkType

  if (linkType === 'iframe') {
    return campaign.slug ? `/campaigns/${campaign.slug}` : '#'
  }

  const link = campaign.link
  if (!link?.value) return '#'
  const value = link.value
  if (typeof value === 'string') return '#'
  return generateHref(value)
}
